/* eslint-disable react/react-in-jsx-scope */
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "@/components/ui/use-toast";
import usePostData from "@/hooks/usePostData";
import i18n from "@/i18n";
import { cn } from "@/libs/utils";
import { usePreviewPerformance } from "@/store/store";
import { Indicator } from "@/types";
import { useQueryClient } from "@tanstack/react-query";
import {
  ChevronDown,
  ChevronLeft,
  ChevronRight,
  Maximize2,
  Minimize2,
} from "lucide-react";
import { FC, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams, useSearchParams } from "react-router-dom";
import ChartGlobal from "./ChartGlobal";

const ChartItemGlobal: FC<{
  dataReporting: any;
  minDate: any;
  maxDate: any;
  indicator: Indicator;
  className?: string;
  currentYear: number;
  width: string;
  maxTco2Year: number;
  context?: "dashboard" | "project" | "programme" | null;
  defaultPreview?: "1" | "2" | "3";
  reportingQueryKey?: readonly unknown[];
}> = ({
  dataReporting,
  minDate,
  maxDate,
  indicator,
  className,
  currentYear: currentYearProp,
  width = "basis-1/2",
  maxTco2Year,
  context = null,
  defaultPreview,
  reportingQueryKey,
}) => {
    const [size, setSize] = useState(width);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const indicatorCumulativeValue = Boolean(
      (context === "dashboard"
        ? (indicator as any)?.cumulativeDashboard ?? false
        : context === "programme"
          ? (indicator as any)?.cumulativeProgram ?? false
          : (indicator as any)?.cumulativeReporting) ??
      (indicator as any)?.cumulative ??
      false
    );
    const [isCumulative, setIsCumulative] = useState(
      indicatorCumulativeValue
    );

    // useEffect(() => {

    //   if (indicator.id == 3) console.log("context", context, indicator.cumulativeProgram, isCumulative)
    // }, [isCumulative])
    const { previews, setPreviews } = usePreviewPerformance();
    const { postData } = usePostData<any>();
    const queryClient = useQueryClient();
    const [isUpdatingCumulative, setIsUpdatingCumulative] = useState(false);
    const [dateArray, setDateArray] = useState<number[]>([]);
    const [searchParams, setSearchParams] = useSearchParams();
    const getClampedYear = (year?: number | null) => {
      const fallbackYear =
        currentYearProp ??
        (maxDate > new Date().getFullYear() ? new Date().getFullYear() : maxDate);
      const candidate = Number.isFinite(year) ? Number(year) : fallbackYear;
      return Math.min(Math.max(candidate, minDate), maxDate);
    };
    const [currentYear, setCurrentYear] = useState(() => {
      const urlYear = Number(searchParams.get("year"));
      return getClampedYear(Number.isNaN(urlYear) ? currentYearProp : urlYear);
    });
    const progIdParam = useParams().id;
    const progId = progIdParam ? parseInt(progIdParam, 10) : undefined;
    const { t } = useTranslation("dashboard", {
      keyPrefix: "program",
    });

    useEffect(() => {
      const DateArrayTemp: number[] = [];
      for (let i = minDate; i <= maxDate; i++) {
        DateArrayTemp.push(i);
      }
      setDateArray(DateArrayTemp);
    }, [minDate, maxDate]);
    useEffect(() => {
      const urlYear = Number(searchParams.get("year"));
      setCurrentYear(
        getClampedYear(Number.isNaN(urlYear) ? currentYearProp : urlYear)
      );
    }, [searchParams, currentYearProp, minDate, maxDate]);
    useEffect(() => {
      const isTco2 = /^tco2_sequestrees/.test(indicator.code);
      const fallback = isTco2 ? "3" : "1";
      setPreviews({ name: indicator.name, value: defaultPreview ?? fallback });
    }, []);
    useEffect(() => {
      setIsCumulative(indicatorCumulativeValue);
    }, [indicatorCumulativeValue]);
    const handleChangeCumulative = async (value: string) => {
      const nextValue = value === "1";
      const previousValue = isCumulative;

      setIsCumulative(nextValue);
      setIsUpdatingCumulative(true);

      const payload =
        context === "programme"
          ? {
            currentItem: {
              id: indicator.id,
              prog_id: progId,
            },
            cumulativeProgram: nextValue,
          }
          : context === "dashboard"
            ? {
              currentItem: {
                id: indicator.id,
              },
              cumulativeDashboard: nextValue,
            }
            : {
              currentItem: {
                id: indicator.id,
              },
              cumulativeReporting: nextValue,
            };
      const response = await postData({
        url: `${import.meta.env.VITE_API_ENDPOINT}/parameter/save/cumul`,
        data: payload,
      });

      if (!response) {
        setIsCumulative(previousValue);
        setIsUpdatingCumulative(false);
        toast({
          className: "text-red-500",
          title: "Une erreur est survenue lors de la sauvegarde.",
        });
        return;
      }

      toast({
        title: "Paramètre sauvegardé",
        description: "Le mode de cumul a bien été mis à jour.",
      });
      if (reportingQueryKey && reportingQueryKey.length > 0) {
        await queryClient.refetchQueries({
          queryKey: [...reportingQueryKey],
          type: "active",
        });
      } else {
        await queryClient.invalidateQueries({
          queryKey: ["reportDataDash"],
        });
      }
      setIsUpdatingCumulative(false);

    };
    const updateCurrentYear = (year: number) => {
      const nextYear = getClampedYear(year);
      setCurrentYear(nextYear);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set("year", nextYear.toString());
        return next;
      });
    };
    const handleYearChange = (action: "prev" | "next") => {
      const year = currentYear;
      updateCurrentYear(action === "prev" ? year - 1 : year + 1);
    };

    const isVisible = true;

    if (!isVisible) return null;

    return (
      <div className={cn(`mb-4 ${size}`, className)}>
        <div className="group relative rounded-[20px] bg-card p-4 px-0 items-center">
          <h3
            className={`${isCollapsed ? "mb-4" : ""
              } ml-8 font-semibold text-muted-foreground flex flex-wrap items-center gap-2 cursor-pointer`}
            onClick={() => setIsCollapsed(!isCollapsed)}
          >
            <ChevronDown size={17} />
            {indicator.indicatorLangs?.find(
              (ptl) => ptl.lang.code === i18n.language
            )
              ? indicator.indicatorLangs?.find(
                (ptl) => ptl.lang.code === i18n.language
              )?.name
              : indicator.name}{" "}

          </h3>
          <div className=" flex items-center">
            <div className=" absolute right-10 top-2 flex items-center gap-2">
              <Select
                value={isCumulative ? "1" : "0"}
                onValueChange={handleChangeCumulative}
                disabled={isUpdatingCumulative}
              >
                <SelectTrigger className="text-foreground/90 outline-none focus:ring-0 focus:ring-transparent w-36 bg-card border-none">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="1">Cumulatif</SelectItem>
                    <SelectItem value="0">Non cumulatif</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
              Affichage :{" "}
              <Select
                onValueChange={(e) => {
                  setPreviews({ name: indicator.name, value: e });
                }}
              >
                <SelectTrigger className="text-foreground/90 outline-none focus:ring-0 focus:ring-transparent w-28 bg-card border-none">
                  <SelectValue
                    placeholder={
                      previews[indicator.name] == "1"
                        ? "Mois"
                        : previews[indicator.name] == "2"
                          ? "Trimestre"
                          : "Année"
                    }
                  />
                </SelectTrigger>
                <SelectContent>
                  <SelectGroup>
                    <SelectItem value="1">Mois</SelectItem>
                  </SelectGroup>
                  <SelectGroup>
                    <SelectItem value="2">Trimestre</SelectItem>
                    <SelectItem value="3">Année</SelectItem>
                  </SelectGroup>
                </SelectContent>
              </Select>
            </div>
            <button
              className=" absolute right-4 top-4 pt-1"
              onClick={() =>
                setSize((prev) => (prev == "w-1/2" ? "w-full" : "w-1/2"))
              }
            >
              {size == "w-1/2" ? (
                <Maximize2 size={15} className="text-gray-700" />
              ) : (
                <Minimize2 size={15} className="text-gray-700" />
              )}
            </button>
          </div>
          {isCollapsed && (
            <>
              <ChartGlobal
                dataReporting={dataReporting}
                minDate={minDate}
                maxDate={maxDate}
                indicator={indicator}
                currentYear={currentYear}
                maxTco2Year={maxTco2Year}
                option={previews[indicator.name]}
                context={context}
                isCumulative={isCumulative}
                externalLoading={isUpdatingCumulative}
              />
              <Indice />
              <div className="flex gap-2 items-center justify-end mr-3">
                <div className="flex gap-1">
                  <Button
                    onClick={() => handleYearChange("prev")}
                    className="flex size-8 items-center justify-center rounded-full bg-card p-0 transition-all hover:bg-gray-200"
                    disabled={currentYear - 1 < minDate}
                  >
                    <ChevronLeft className="text-foreground/70" />
                  </Button>
                  <Button
                    onClick={() => handleYearChange("next")}
                    className="flex size-8 items-center justify-center rounded-full bg-card p-0 transition-all hover:bg-gray-200"
                    disabled={currentYear + 1 > maxDate}
                  >
                    <ChevronRight className="text-foreground/70" />
                  </Button>
                </div>
                <Select
                  value={currentYear.toString()}
                  onValueChange={(e) => {
                    updateCurrentYear(Number(e));
                  }}
                >
                  <SelectTrigger className="w-16 border-0 px-0 text-lg text-foreground/90 outline-none focus:ring-0 focus:ring-transparent bg-card">
                    <SelectValue placeholder={currentYear?.toString()} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectGroup>
                      {dateArray.map((i, k) => (
                        <SelectItem value={i.toString()} key={k}>
                          {i}
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>
      </div>
    );
  };

const Indice = () => {
  const { t } = useTranslation("dashboard", {
    keyPrefix: "program",
  });
  return (
    <div className="ml-10 mt-5 flex items-center gap-3">
      <div className="flex items-center gap-1">
        <div className={`size-2 rounded-full bg-verdeoblue`}></div>
        {t("Prévu")}
      </div>
      <div className="flex items-center gap-1">
        <div className={`size-2 rounded-full bg-verdeogreen`}></div>
        {t("Réalisé")}
      </div>
      <div className="flex items-center gap-1">
        <div className={`size-2 rounded-full bg-verdeoorange`}></div>
        {t("Verifié / justifié / certifié")}
      </div>
    </div>
  );
};

export default ChartItemGlobal;
